/** Small, allocation-free math helpers shared by sim and render. */

export const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
export const clamp01 = (v: number) => clamp(v, 0, 1);
export const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
export const invLerp = (a: number, b: number, v: number) => (b === a ? 0 : (v - a) / (b - a));
export const smoothstep = (e0: number, e1: number, x: number) => {
  const t = clamp01(invLerp(e0, e1, x));
  return t * t * (3 - 2 * t);
};
export const smootherstep = (e0: number, e1: number, x: number) => {
  const t = clamp01(invLerp(e0, e1, x));
  return t * t * t * (t * (t * 6 - 15) + 10);
};

/** Frame-rate independent exponential approach. `rate` is 1/seconds. */
export const damp = (a: number, b: number, rate: number, dt: number) =>
  b + (a - b) * Math.exp(-rate * dt);

/** Signed value moved toward a target by at most `maxDelta`. */
export const moveToward = (a: number, b: number, maxDelta: number) => {
  const d = b - a;
  return Math.abs(d) <= maxDelta ? b : a + Math.sign(d) * maxDelta;
};

export const sign = (v: number) => (v > 0 ? 1 : v < 0 ? -1 : 0);
export const sqr = (v: number) => v * v;

/** Deadzone + expo curve for analog sticks; keeps full range after the deadzone. */
export function shapeAxis(raw: number, deadzone = 0.08, expo = 0.35): number {
  const m = Math.abs(raw);
  if (m <= deadzone) return 0;
  const n = (m - deadzone) / (1 - deadzone);
  const shaped = n * (1 - expo) + n * n * n * expo;
  return shaped * Math.sign(raw);
}

/** Wrap an angle to (-PI, PI]. */
export function wrapPi(a: number): number {
  a = (a + Math.PI) % (Math.PI * 2);
  if (a < 0) a += Math.PI * 2;
  return a - Math.PI;
}

/** Deterministic 32-bit PRNG (mulberry32). Seeded worlds must use this, never Math.random. */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return function () {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 2D value hash in [0,1). Deterministic, no state. */
export function hash2(x: number, y: number, seed = 0): number {
  let h = Math.imul(x | 0, 0x27d4eb2d) ^ Math.imul(y | 0, 0x165667b1) ^ Math.imul(seed, 0x9e3779b9);
  h = Math.imul(h ^ (h >>> 15), 0x85ebca6b);
  h = Math.imul(h ^ (h >>> 13), 0xc2b2ae35);
  return ((h ^ (h >>> 16)) >>> 0) / 4294967296;
}

/** Guard against NaN/Inf leaking through the sim; returns fallback and flags a fault. */
export function finiteOr(v: number, fallback: number): number {
  return Number.isFinite(v) ? v : fallback;
}
